import { queryOptions } from "@tanstack/react-query";
import { api } from "./api";
import type {
  Account,
  AgingRow,
  APInvoice,
  ARInvoice,
  ApprovalAnalytics,
  ApprovalControlsSummary,
  ApprovalPolicy,
  ApprovalRecommendation,
  ApprovalRequest,
  ApprovalRequestDetail,
  BlanketSummary,
  POApproval,
  POReceipt,
  PurchaseOrder,
  AuditLogFilters,
  AuditLogPage,
  AvailableEntity,
  BankAccount,
  BankReconciliation,
  BankTransaction,
  CashFlowStatement,
  CloseChecklist,
  NumberingConfig,
  NumberingDimension,
  Contact,
  Contract,
  ContractEvent,
  CostCode,
  CostCodeListPage,
  CostCodeMapping,
  CostCodeReportRow,
  CostCodeTemplate,
  CostCodeTemplateNode,
  Entity,
  FieldSuggestion,
  IntercompanyBalanceReport,
  IntercompanyPair,
  IntercompanyTransaction,
  FiscalPeriod,
  FiscalYear,
  GLLineRow,
  GLSummary,
  JobCostSummaryRow,
  WipRow,
  CommittedCostRow,
  ProjectProfitabilityRow,
  MetricsSnapshot,
  Party,
  PartyListPage,
  PartyType,
  Payment,
  PaymentDirection,
  Plan,
  PriceComponentOut,
  SubscriptionPriceOverrideOut,
  RecognitionSchedule,
  SecretStatus,
  Subscription,
  TrialBalanceResponse,
  User,
  UserEntityAccess,
  SubPayApplication,
  SubPayAppListItem,
  PayrollEmployee,
  PayRun,
  Paycheck,
  EarningCode,
  LienWaiver,
  LienWaiverStateSummary,
  LienWaiverTemplateOut,
  Subcontract,
  PartyAddress,
  ContractWorkPackageMode,
  ContractWorkPackageStatus,
  WorkPackageListPage,
  WorkPackageReview,
  WorkPackageVersionListItem,
} from "./types";

export interface WorkPackageListParams {
  status?: ContractWorkPackageStatus;
  mode?: ContractWorkPackageMode;
  projectId?: string;
  search?: string;
  sort?: "updated_at" | "created_at" | "contract_no" | "project_code" | "status";
  order?: "asc" | "desc";
  limit?: number;
  offset?: number;
}

export const workPackageKeys = {
  all: ["contract-work-packages"] as const,
  list: (params: WorkPackageListParams) =>
    [...workPackageKeys.all, "list", params] as const,
  review: (id: string) => [...workPackageKeys.all, "review", id] as const,
  versions: (id: string) => [...workPackageKeys.all, "versions", id] as const,
};

export const workPackageListQueryOptions = (params: WorkPackageListParams) => {
  const search = new URLSearchParams();
  if (params.status) search.set("status", params.status);
  if (params.mode) search.set("mode", params.mode);
  if (params.projectId) search.set("project_id", params.projectId);
  if (params.search) search.set("search", params.search);
  search.set("sort", params.sort ?? "updated_at");
  search.set("order", params.order ?? "desc");
  search.set("limit", String(params.limit ?? 50));
  search.set("offset", String(params.offset ?? 0));
  return queryOptions({
    queryKey: workPackageKeys.list(params),
    queryFn: () =>
      api<WorkPackageListPage>(`/api/contract-work-packages?${search.toString()}`),
    placeholderData: (previous) => previous,
  });
};

export const workPackageReviewQueryOptions = (id: string) =>
  queryOptions({
    queryKey: workPackageKeys.review(id),
    queryFn: () => api<WorkPackageReview>(`/api/contract-work-packages/${id}/review`),
    enabled: Boolean(id),
  });

export const workPackageVersionsQueryOptions = (id: string) =>
  queryOptions({
    queryKey: workPackageKeys.versions(id),
    queryFn: () =>
      api<WorkPackageVersionListItem[]>(
        `/api/contract-work-packages/${id}/versions?limit=100`,
      ),
    enabled: Boolean(id),
  });

export const authKeys = {
  me: () => ["auth", "me"] as const,
  myEntities: () => ["auth", "me", "entities"] as const,
};

export const meQueryOptions = () =>
  queryOptions({
    queryKey: authKeys.me(),
    queryFn: () => api<User>("/api/auth/me"),
    staleTime: 60_000,
  });

/** Implementation packs installed in this company's database, plus the industry it
 *  was set up for (INDUSTRY-PACK-INSTALL-001). Pack menus and tours follow it. */
export const implementationInstalledQueryOptions = () =>
  queryOptions({
    queryKey: ["implementation", "installed"],
    queryFn: () =>
      api<{
        industry: string | null;
        label: string | null;
        implementation_packs: string[];
        /** Every pack active for the current entity; drives pack-gated menus. */
        active_packs?: string[];
        /** Industry-specific menu layouts / home-dashboard modes on offer. */
        industry_layouts?: string[];
      }>(
        "/api/implementation/installed",
      ),
    staleTime: 5 * 60_000,
    meta: { skipErrorToast: true },
  });

export const myEntitiesQueryOptions = () =>
  queryOptions({
    queryKey: authKeys.myEntities(),
    queryFn: () => api<AvailableEntity[]>("/api/auth/me/entities"),
    staleTime: 30_000,
  });

export interface OrganizationSummary {
  base_currency_code: string;
}

export const organizationCurrentQueryOptions = () =>
  queryOptions({
    queryKey: ["organization", "current"] as const,
    queryFn: () => api<OrganizationSummary>("/api/organization"),
    staleTime: 5 * 60_000,
  });

// ─────────────────────────────────────────────────────────────
// Multi-entity Phase A — entity admin
// ─────────────────────────────────────────────────────────────
export const entityKeys = {
  all: ["entities"] as const,
  list: () => [...entityKeys.all, "list"] as const,
  detail: (id: string) => [...entityKeys.all, "detail", id] as const,
  users: (id: string) => [...entityKeys.all, "users", id] as const,
};

export const entitiesListQueryOptions = (includeInactive = false) =>
  queryOptions({
    queryKey: includeInactive
      ? ([...entityKeys.list(), { include_inactive: true }] as const)
      : entityKeys.list(),
    queryFn: () =>
      api<Entity[]>(
        `/api/entities${includeInactive ? "?include_inactive=true" : ""}`,
      ),
  });

export const entityDetailQueryOptions = (id: string) =>
  queryOptions({
    queryKey: entityKeys.detail(id),
    queryFn: () => api<Entity>(`/api/entities/${id}`),
    enabled: Boolean(id),
  });

export const entityUsersQueryOptions = (entityId: string) =>
  queryOptions({
    queryKey: entityKeys.users(entityId),
    queryFn: () =>
      api<UserEntityAccess[]>(`/api/entities/${entityId}/users`),
    enabled: Boolean(entityId),
  });

// ─────────────────────────────────────────────────────────────
// Bank Accounts — multi-bank, multi-currency cash management
// ─────────────────────────────────────────────────────────────
export const bankAccountKeys = {
  all: ["bank-accounts"] as const,
  list: () => [...bankAccountKeys.all, "list"] as const,
  detail: (id: string) => [...bankAccountKeys.all, "detail", id] as const,
  transactions: (id: string) =>
    [...bankAccountKeys.all, "transactions", id] as const,
};

export const bankAccountsListQueryOptions = (includeInactive = false) =>
  queryOptions({
    queryKey: includeInactive
      ? [...bankAccountKeys.list(), { include_inactive: true }]
      : bankAccountKeys.list(),
    queryFn: () =>
      api<BankAccount[]>(
        includeInactive
          ? "/api/bank-accounts?include_inactive=true"
          : "/api/bank-accounts",
      ),
  });

export const bankAccountDetailQueryOptions = (id: string) =>
  queryOptions({
    queryKey: bankAccountKeys.detail(id),
    queryFn: () => api<BankAccount>(`/api/bank-accounts/${id}`),
    enabled: Boolean(id),
  });

export interface BankAccountBalance {
  bank_account_id: string;
  currency_code: string;
  current_balance: string;
  as_of: string;
}

export const bankAccountBalanceQueryOptions = (
  bankAccountId: string | undefined,
) =>
  queryOptions({
    queryKey: [...bankAccountKeys.all, "balance", bankAccountId ?? ""] as const,
    queryFn: () =>
      api<BankAccountBalance>(
        `/api/bank-accounts/${bankAccountId}/balance`,
      ),
    enabled: Boolean(bankAccountId),
    staleTime: 30_000,
    refetchInterval: 30_000,
  });

export const bankAccountTransactionsQueryOptions = (id: string, limit = 200) =>
  queryOptions({
    queryKey: [...bankAccountKeys.transactions(id), { limit }] as const,
    queryFn: () =>
      api<BankTransaction[]>(
        `/api/bank-accounts/${id}/transactions?limit=${limit}`,
      ),
    enabled: Boolean(id),
  });


// ─────────────────────────────────────────────────────────────
// Phase B — Intercompany
// ─────────────────────────────────────────────────────────────
export const icKeys = {
  all: ["intercompany"] as const,
  pairs: () => [...icKeys.all, "pairs"] as const,
  transactions: (entityId?: string) =>
    entityId
      ? ([...icKeys.all, "transactions", { entity_id: entityId }] as const)
      : ([...icKeys.all, "transactions"] as const),
  balances: (asOfDate?: string) =>
    asOfDate
      ? ([...icKeys.all, "balances", { as_of_date: asOfDate }] as const)
      : ([...icKeys.all, "balances"] as const),
};

export const icPairsQueryOptions = () =>
  queryOptions({
    queryKey: icKeys.pairs(),
    queryFn: () => api<IntercompanyPair[]>("/api/intercompany/pairs"),
  });

export const icTransactionsQueryOptions = (entityId?: string) =>
  queryOptions({
    queryKey: icKeys.transactions(entityId),
    queryFn: () =>
      api<IntercompanyTransaction[]>(
        entityId
          ? `/api/intercompany/transactions?entity_id=${entityId}`
          : "/api/intercompany/transactions",
      ),
  });

export const icBalancesQueryOptions = (asOfDate?: string) =>
  queryOptions({
    queryKey: icKeys.balances(asOfDate),
    queryFn: () =>
      api<IntercompanyBalanceReport>(
        asOfDate
          ? `/api/intercompany/balances?as_of_date=${asOfDate}`
          : "/api/intercompany/balances",
      ),
  });

export const contractKeys = {
  all: ["contracts"] as const,
  list: () => [...contractKeys.all, "list"] as const,
  detail: (id: string) => [...contractKeys.all, "detail", id] as const,
  events: (id: string) => [...contractKeys.all, "events", id] as const,
  fixtures: () => [...contractKeys.all, "fixtures"] as const,
};

export const contractsListQueryOptions = (customerId?: string) =>
  queryOptions({
    queryKey: customerId
      ? ([...contractKeys.list(), { customer_id: customerId }] as const)
      : contractKeys.list(),
    queryFn: () =>
      api<Contract[]>(
        customerId ? `/api/contracts?customer_id=${customerId}` : "/api/contracts",
      ),
  });

export const contractDetailQueryOptions = (id: string) =>
  queryOptions({
    queryKey: contractKeys.detail(id),
    queryFn: () => api<Contract>(`/api/contracts/${id}`),
    enabled: Boolean(id),
  });

export const contractEventsQueryOptions = (id: string) =>
  queryOptions({
    queryKey: contractKeys.events(id),
    queryFn: () => api<ContractEvent[]>(`/api/contracts/${id}/events`),
    enabled: Boolean(id),
  });

export const contractFixturesQueryOptions = () =>
  queryOptions({
    queryKey: contractKeys.fixtures(),
    queryFn: () => api<{ fixtures: string[] }>("/api/contracts/fixtures/list"),
  });

// ─────────────────────────────────────────────────────────────
// Field suggestions — powers free-text Autocomplete dropdowns.
// The same field_key (e.g. "contract.payment_method") maps a UI input
// to (a) hardcoded starter values, (b) values seen in data, (c) values
// the onboarding/admin agent has added.
// ─────────────────────────────────────────────────────────────
export const fieldSuggestionKeys = {
  all: ["field-suggestions"] as const,
  byKey: (fieldKey: string) =>
    [...fieldSuggestionKeys.all, fieldKey] as const,
};

// ─────────────────────────────────────────────────────────────
// General Ledger
// ─────────────────────────────────────────────────────────────
export interface GLFilters {
  from_date?: string | null;
  to_date?: string | null;
  account_code?: string | null;
  account_type?: string | null;
  project_id?: string | null;
  status?: string | null;
  source?: string | null;
  search?: string | null;
  include_drafts?: boolean;
  limit: number;
  offset: number;
  sort: string;
}

export const glKeys = {
  all: ["gl"] as const,
  lines: (filters: GLFilters) =>
    [...glKeys.all, "lines", filters] as const,
  summary: (filters: Partial<GLFilters>) =>
    [...glKeys.all, "summary", filters] as const,
};

function toQuery(filters: object): string {
  const sp = new URLSearchParams();
  for (const [k, v] of Object.entries(filters)) {
    if (v === null || v === undefined || v === "") continue;
    sp.set(k, String(v));
  }
  return sp.toString();
}

export const glLinesQueryOptions = (filters: GLFilters) =>
  queryOptions({
    queryKey: glKeys.lines(filters),
    queryFn: () =>
      api<GLLineRow[]>(`/api/gl/lines?${toQuery(filters)}`),
    placeholderData: (prev) => prev,
  });

export const glSummaryQueryOptions = (filters: Partial<GLFilters>) =>
  queryOptions({
    queryKey: glKeys.summary(filters),
    queryFn: () => api<GLSummary>(`/api/gl/summary?${toQuery(filters)}`),
  });

// ─────────────────────────────────────────────────────────────
// Journals (single-journal detail; list lives under /api/gl/lines)
// ─────────────────────────────────────────────────────────────
export interface JournalLine {
  id: string;
  line_no: number;
  account_id: string;
  debit: string;
  credit: string;
  currency_code: string;
  fx_rate: string;
  base_debit: string;
  base_credit: string;
  project_id: string | null;
  phase_id: string | null;
  cost_code_id: string | null;
  cost_type: string | null;
  cost_type_id: string | null;
  quantity: string | null;
  party_id: string | null;
  tax_code_id: string | null;
  dimensions: Record<string, unknown> | null;
  memo: string | null;
}

export interface Journal {
  id: string;
  journal_no: number;
  journal_date: string;
  period_id: string;
  source: string;
  description: string | null;
  status: "draft" | "posted" | "reversed";
  posted_at: string | null;
  reversed_at: string | null;
  reverses_journal_id: string | null;
  lines: JournalLine[];
}

export interface JournalDetail extends Journal {
  can_reverse: boolean;
  reversal_block_reason: string | null;
}

export const journalKeys = {
  all: ["journals"] as const,
  detail: (id: string) => [...journalKeys.all, "detail", id] as const,
};

export const journalDetailQueryOptions = (id: string) =>
  queryOptions({
    queryKey: journalKeys.detail(id),
    queryFn: () => api<JournalDetail>(`/api/journals/${id}`),
    enabled: Boolean(id),
  });

// ─────────────────────────────────────────────────────────────
// Fiscal periods — used by Trial Balance period filter + Manual JE form
// ─────────────────────────────────────────────────────────────
export const periodKeys = {
  all: ["periods"] as const,
  list: () => [...periodKeys.all, "list"] as const,
  years: () => [...periodKeys.all, "years"] as const,
  closeChecklist: (periodId: string) =>
    [...periodKeys.all, "close-checklist", periodId] as const,
};

export const periodsListQueryOptions = () =>
  queryOptions({
    queryKey: periodKeys.list(),
    queryFn: () => api<FiscalPeriod[]>("/api/periods"),
    staleTime: 5 * 60_000,
  });

export const fiscalYearsListQueryOptions = () =>
  queryOptions({
    queryKey: periodKeys.years(),
    queryFn: () => api<FiscalYear[]>("/api/periods/years"),
    staleTime: 5 * 60_000,
  });

/** Pre-flight checklist for closing a period. Read-only — safe to poll. */
export const closeChecklistQueryOptions = (periodId: string | null) =>
  queryOptions({
    queryKey: periodKeys.closeChecklist(periodId ?? ""),
    queryFn: () =>
      api<CloseChecklist>(`/api/periods/${periodId}/close-checklist`),
    enabled: Boolean(periodId),
    // Refetch on focus — the user might post a new draft journal in
    // another tab and come back wondering why the blocker won't clear.
    refetchOnWindowFocus: true,
    staleTime: 0,
  });

// ─────────────────────────────────────────────────────────────
// Audit log viewer — admin-only history of every entity-scoped change
// (DB trigger trg_audit populates the underlying table).
// ─────────────────────────────────────────────────────────────
export const auditLogKeys = {
  all: ["audit-log"] as const,
  list: (filters: AuditLogFilters) =>
    [...auditLogKeys.all, "list", filters] as const,
};

export const auditLogQueryOptions = (filters: AuditLogFilters) =>
  queryOptions({
    queryKey: auditLogKeys.list(filters),
    queryFn: () => {
      const params = new URLSearchParams();
      if (filters.table_name) params.set("table_name", filters.table_name);
      if (filters.row_id) params.set("row_id", filters.row_id);
      if (filters.action) params.set("action", filters.action);
      if (filters.user_id) params.set("user_id", filters.user_id);
      if (filters.from_date) params.set("from_date", filters.from_date);
      if (filters.to_date) params.set("to_date", filters.to_date);
      params.set("limit", String(filters.limit ?? 100));
      params.set("offset", String(filters.offset ?? 0));
      const qs = params.toString();
      return api<AuditLogPage>(`/api/audit-log${qs ? `?${qs}` : ""}`);
    },
    // Audit data is append-only and ordered by `at desc` — staleness for
    // 60s is harmless, and snappier than spamming the DB on every tab.
    staleTime: 60_000,
  });

// ─────────────────────────────────────────────────────────────
// Trial Balance
// ─────────────────────────────────────────────────────────────
export const trialBalanceKeys = {
  all: ["trial-balance"] as const,
  byPeriod: (periodId: string | null, dimCode?: string, dimValueId?: string) =>
    [...trialBalanceKeys.all, periodId ?? "all", dimCode ?? "", dimValueId ?? ""] as const,
};

export const trialBalanceQueryOptions = (
  periodId: string | null,
  dimCode?: string,
  dimValueId?: string,
) =>
  queryOptions({
    queryKey: trialBalanceKeys.byPeriod(periodId, dimCode, dimValueId),
    queryFn: () => {
      const p = new URLSearchParams();
      if (periodId) p.set("period_id", periodId);
      if (dimCode) p.set("dimension_code", dimCode);
      if (dimValueId) p.set("dimension_value_id", dimValueId);
      const qs = p.toString();
      return api<TrialBalanceResponse>(`/api/reports/trial-balance${qs ? `?${qs}` : ""}`);
    },
  });

// ─────────────────────────────────────────────────────────────
// Balance Sheet (snapshot of A/L/E as of a date)
// Income Statement (P&L for a date range)
// ─────────────────────────────────────────────────────────────
import type { BalanceSheet, IncomeStatement } from "./types";

export const balanceSheetKeys = {
  all: ["balance-sheet"] as const,
  asOf: (date: string) => [...balanceSheetKeys.all, date] as const,
};

export const balanceSheetQueryOptions = (asOfDate: string) =>
  queryOptions({
    queryKey: balanceSheetKeys.asOf(asOfDate),
    queryFn: () =>
      api<BalanceSheet>(`/api/reports/balance-sheet?as_of=${asOfDate}`),
    enabled: Boolean(asOfDate),
  });

export const consolidatedBalanceSheetQueryOptions = (
  parentEntityId: string, asOfDate: string,
) =>
  queryOptions({
    queryKey: [...balanceSheetKeys.all, "consolidated", parentEntityId, asOfDate] as const,
    queryFn: () =>
      api<BalanceSheet>(
        `/api/reports/consolidated/balance-sheet?parent_entity_id=${parentEntityId}&as_of=${asOfDate}`,
      ),
    enabled: Boolean(parentEntityId && asOfDate),
  });

export const incomeStatementKeys = {
  all: ["income-statement"] as const,
  range: (from: string, to: string, dimCode?: string, dimValueId?: string) =>
    [...incomeStatementKeys.all, from, to, dimCode ?? "", dimValueId ?? ""] as const,
};

export const incomeStatementQueryOptions = (
  from: string,
  to: string,
  dimCode?: string,
  dimValueId?: string,
) =>
  queryOptions({
    queryKey: incomeStatementKeys.range(from, to, dimCode, dimValueId),
    queryFn: () => {
      const p = new URLSearchParams({ period_from: from, period_to: to });
      if (dimCode) p.set("dimension_code", dimCode);
      if (dimValueId) p.set("dimension_value_id", dimValueId);
      return api<IncomeStatement>(`/api/reports/income-statement?${p.toString()}`);
    },
    enabled: Boolean(from && to),
  });

// ─────────────────────────────────────────────────────────────
// Cash Flow Statement (indirect method)
// ─────────────────────────────────────────────────────────────
export const cashFlowKeys = {
  all: ["cash-flow"] as const,
  range: (from: string, to: string) =>
    [...cashFlowKeys.all, "range", from, to] as const,
};

export const cashFlowQueryOptions = (from: string, to: string) =>
  queryOptions({
    queryKey: cashFlowKeys.range(from, to),
    queryFn: () =>
      api<CashFlowStatement>(
        `/api/reports/cash-flow?period_from=${from}&period_to=${to}`,
      ),
    enabled: Boolean(from && to),
  });


export const consolidatedIncomeStatementQueryOptions = (
  parentEntityId: string, from: string, to: string,
) =>
  queryOptions({
    queryKey: [
      ...incomeStatementKeys.all, "consolidated", parentEntityId, from, to,
    ] as const,
    queryFn: () =>
      api<IncomeStatement>(
        `/api/reports/consolidated/income-statement?parent_entity_id=${parentEntityId}&period_from=${from}&period_to=${to}`,
      ),
    enabled: Boolean(parentEntityId && from && to),
  });


// ─────────────────────────────────────────────────────────────
// Job Cost + WIP (existing DB views)
// ─────────────────────────────────────────────────────────────
export const jobCostKeys = {
  all: ["job-cost"] as const,
  summary: (projectId?: string) =>
    projectId
      ? ([...jobCostKeys.all, "summary", projectId] as const)
      : ([...jobCostKeys.all, "summary"] as const),
  wip: (projectId?: string) =>
    projectId
      ? ([...jobCostKeys.all, "wip", projectId] as const)
      : ([...jobCostKeys.all, "wip"] as const),
  committed: (projectId?: string) =>
    projectId
      ? ([...jobCostKeys.all, "committed", projectId] as const)
      : ([...jobCostKeys.all, "committed"] as const),
  profitability: (projectId?: string) =>
    projectId
      ? ([...jobCostKeys.all, "profitability", projectId] as const)
      : ([...jobCostKeys.all, "profitability"] as const),
};

export const jobCostSummaryQueryOptions = (projectId?: string) =>
  queryOptions({
    queryKey: jobCostKeys.summary(projectId),
    queryFn: () =>
      api<JobCostSummaryRow[]>(
        projectId
          ? `/api/reports/job-cost-summary?project_id=${projectId}`
          : "/api/reports/job-cost-summary",
      ),
  });

export const wipQueryOptions = (projectId?: string) =>
  queryOptions({
    queryKey: jobCostKeys.wip(projectId),
    queryFn: () =>
      api<WipRow[]>(
        projectId
          ? `/api/reports/wip?project_id=${projectId}`
          : "/api/reports/wip",
      ),
  });

export const committedCostQueryOptions = (projectId?: string) =>
  queryOptions({
    queryKey: jobCostKeys.committed(projectId),
    queryFn: () =>
      api<CommittedCostRow[]>(
        projectId
          ? `/api/reports/committed-cost?project_id=${projectId}`
          : "/api/reports/committed-cost",
      ),
  });

export const projectProfitabilityQueryOptions = (projectId?: string) =>
  queryOptions({
    queryKey: jobCostKeys.profitability(projectId),
    queryFn: () =>
      api<ProjectProfitabilityRow[]>(
        projectId
          ? `/api/reports/project-profitability?project_id=${projectId}`
          : "/api/reports/project-profitability",
      ),
  });

// ─────────────────────────────────────────────────────────────
// Accounts — chart of accounts, used to label invoice/journal lines
// ─────────────────────────────────────────────────────────────
export interface AccountSummary {
  id: string;
  code: string;
  name: string;
  account_type: string;
  is_postable: boolean;
  currency_code: string | null;
}

export const accountKeys = {
  all: ["accounts"] as const,
  list: () => [...accountKeys.all, "list"] as const,
};

export const accountsListQueryOptions = () =>
  queryOptions({
    queryKey: accountKeys.list(),
    queryFn: () => api<AccountSummary[]>("/api/accounts"),
    staleTime: 5 * 60_000,
  });

// Full chart of accounts (includes inactive — for the admin CoA page)
export const accountsFullListQueryOptions = (
  opts: { active_only?: boolean } = {},
) =>
  queryOptions({
    queryKey: [...accountKeys.all, "full", opts] as const,
    queryFn: () => {
      const sp = new URLSearchParams();
      if (opts.active_only !== undefined) sp.set("active_only", String(opts.active_only));
      return api<Account[]>(`/api/accounts?${sp.toString()}`);
    },
  });

// ─────────────────────────────────────────────────────────────
// Parties (customers / vendors / employees)
// ─────────────────────────────────────────────────────────────
export const partyKeys = {
  all: ["parties"] as const,
  list: (filters: {
    party_type?: PartyType;
    active_only?: boolean;
    search?: string;
    limit?: number;
    offset?: number;
  }) => [...partyKeys.all, "list", filters] as const,
  detail: (id: string) => [...partyKeys.all, "detail", id] as const,
};

const partiesUrl = (filters: {
  party_type?: PartyType;
  active_only?: boolean;
  search?: string;
  limit?: number;
  offset?: number;
}) => {
  const sp = new URLSearchParams();
  if (filters.party_type) sp.set("party_type", filters.party_type);
  if (filters.active_only !== undefined)
    sp.set("active_only", String(filters.active_only));
  if (filters.search) sp.set("search", filters.search);
  if (filters.limit !== undefined) sp.set("limit", String(filters.limit));
  if (filters.offset) sp.set("offset", String(filters.offset));
  return `/api/parties?${sp.toString()}`;
};

/** Flat list (no limit → server returns the full matching set).
 *  Existing grid/dropdown callers keep their Party[] shape. */
export const partiesListQueryOptions = (
  filters: { party_type?: PartyType; active_only?: boolean } = {},
) =>
  queryOptions({
    queryKey: partyKeys.list(filters),
    queryFn: async () => (await api<PartyListPage>(partiesUrl(filters))).items,
  });

/** Paginated envelope for the vendor/customer typeahead (thousands of
 *  parties — server-side search, first page only). */
export const partiesSearchQueryOptions = (filters: {
  party_type?: PartyType;
  active_only?: boolean;
  search?: string;
  limit?: number;
  offset?: number;
}) =>
  queryOptions({
    queryKey: partyKeys.list({ ...filters, __page: true } as never),
    queryFn: () => api<PartyListPage>(partiesUrl(filters)),
  });

export const partyDetailQueryOptions = (id: string) =>
  queryOptions({
    queryKey: partyKeys.detail(id),
    queryFn: () => api<Party>(`/api/parties/${id}`),
    enabled: Boolean(id),
  });

// ─────────────────────────────────────────────────────────────
// Contacts (people at a Party, linked to contracts/invoices)
// ─────────────────────────────────────────────────────────────
export const contactKeys = {
  all: ["contacts"] as const,
  forParty: (partyId: string) => [...contactKeys.all, "party", partyId] as const,
};

export const contactsAtPartyQueryOptions = (partyId: string) =>
  queryOptions({
    queryKey: contactKeys.forParty(partyId),
    queryFn: () => api<Contact[]>(`/api/parties/${partyId}/contacts`),
    enabled: Boolean(partyId),
  });

// ─────────────────────────────────────────────────────────────
// AR Invoices
// ─────────────────────────────────────────────────────────────
export const arInvoiceKeys = {
  all: ["ar-invoices"] as const,
  list: (filters: { customer_id?: string; project_id?: string }) =>
    [...arInvoiceKeys.all, "list", filters] as const,
  detail: (id: string) => [...arInvoiceKeys.all, "detail", id] as const,
};

export const arInvoicesListQueryOptions = (
  filters: { customer_id?: string; project_id?: string } = {},
) =>
  queryOptions({
    queryKey: arInvoiceKeys.list(filters),
    queryFn: () => {
      const sp = new URLSearchParams();
      if (filters.customer_id) sp.set("customer_id", filters.customer_id);
      if (filters.project_id) sp.set("project_id", filters.project_id);
      return api<ARInvoice[]>(`/api/ar/invoices?${sp.toString()}`);
    },
  });

export const arInvoiceDetailQueryOptions = (id: string) =>
  queryOptions({
    queryKey: arInvoiceKeys.detail(id),
    queryFn: () => api<ARInvoice>(`/api/ar/invoices/${id}`),
    enabled: Boolean(id),
  });

// ─────────────────────────────────────────────────────────────
// AP Invoices
// ─────────────────────────────────────────────────────────────
export const apInvoiceKeys = {
  all: ["ap-invoices"] as const,
  list: (filters: { vendor_id?: string }) =>
    [...apInvoiceKeys.all, "list", filters] as const,
  detail: (id: string) => [...apInvoiceKeys.all, "detail", id] as const,
};

export const apInvoicesListQueryOptions = (
  filters: { vendor_id?: string } = {},
) =>
  queryOptions({
    queryKey: apInvoiceKeys.list(filters),
    queryFn: () => {
      const sp = new URLSearchParams();
      if (filters.vendor_id) sp.set("vendor_id", filters.vendor_id);
      return api<APInvoice[]>(`/api/ap/invoices?${sp.toString()}`);
    },
  });

export const apInvoiceDetailQueryOptions = (id: string) =>
  queryOptions({
    queryKey: apInvoiceKeys.detail(id),
    queryFn: () => api<APInvoice>(`/api/ap/invoices/${id}`),
    enabled: Boolean(id),
  });

// ─────────────────────────────────────────────────────────────
// Bank reconciliations
// ─────────────────────────────────────────────────────────────
export const bankRecKeys = {
  all: ["bank-reconciliations"] as const,
  list: () => [...bankRecKeys.all, "list"] as const,
  detail: (id: string) => [...bankRecKeys.all, "detail", id] as const,
};

export const bankRecListQueryOptions = () =>
  queryOptions({
    queryKey: bankRecKeys.list(),
    queryFn: () => api<BankReconciliation[]>("/api/bank-reconciliations"),
  });

export const bankRecDetailQueryOptions = (id: string) =>
  queryOptions({
    queryKey: bankRecKeys.detail(id),
    queryFn: () => api<BankReconciliation>(`/api/bank-reconciliations/${id}`),
    enabled: Boolean(id),
  });

// ─────────────────────────────────────────────────────────────
// Aging (AR + AP)
// ─────────────────────────────────────────────────────────────
export const agingKeys = {
  all: ["aging"] as const,
  ar: () => [...agingKeys.all, "ar"] as const,
  ap: () => [...agingKeys.all, "ap"] as const,
};

export const arAgingQueryOptions = () =>
  queryOptions({
    queryKey: agingKeys.ar(),
    queryFn: () => api<AgingRow[]>("/api/reports/ar-aging"),
  });

export const apAgingQueryOptions = () =>
  queryOptions({
    queryKey: agingKeys.ap(),
    queryFn: () => api<AgingRow[]>("/api/reports/ap-aging"),
  });

// ─────────────────────────────────────────────────────────────
// Payments (AR receipts = direction=incoming, AP disbursements = outgoing)
// ─────────────────────────────────────────────────────────────
export const paymentKeys = {
  all: ["payments"] as const,
  list: (filters: { direction?: PaymentDirection; party_id?: string }) =>
    [...paymentKeys.all, "list", filters] as const,
  detail: (id: string) => [...paymentKeys.all, "detail", id] as const,
};

export const paymentsListQueryOptions = (
  filters: { direction?: PaymentDirection; party_id?: string } = {},
) =>
  queryOptions({
    queryKey: paymentKeys.list(filters),
    queryFn: () => {
      const sp = new URLSearchParams();
      if (filters.direction) sp.set("direction", filters.direction);
      if (filters.party_id) sp.set("party_id", filters.party_id);
      return api<Payment[]>(`/api/payments?${sp.toString()}`);
    },
  });

export const paymentDetailQueryOptions = (id: string) =>
  queryOptions({
    queryKey: paymentKeys.detail(id),
    queryFn: () => api<Payment>(`/api/payments/${id}`),
    enabled: Boolean(id),
  });

export const paymentAttachmentKeys = {
  all: ["payment-attachments"] as const,
  list: (paymentId: string) =>
    [...paymentAttachmentKeys.all, "list", paymentId] as const,
};

export const paymentAttachmentsQueryOptions = (paymentId: string) =>
  queryOptions({
    queryKey: paymentAttachmentKeys.list(paymentId),
    queryFn: () =>
      api<import("./types").PaymentAttachment[]>(
        `/api/payments/${paymentId}/attachments`,
      ),
    enabled: Boolean(paymentId),
  });

// ─────────────────────────────────────────────────────────────
// Admin secrets
// ─────────────────────────────────────────────────────────────
export const secretKeys = {
  all: ["admin-secrets"] as const,
  list: () => [...secretKeys.all, "list"] as const,
  detail: (name: string) => [...secretKeys.all, "detail", name] as const,
};

export const secretsListQueryOptions = () =>
  queryOptions({
    queryKey: secretKeys.list(),
    queryFn: () => api<SecretStatus[]>("/api/admin/secrets"),
  });

export const fieldSuggestionsQueryOptions = (fieldKey: string) =>
  queryOptions({
    queryKey: fieldSuggestionKeys.byKey(fieldKey),
    queryFn: () =>
      api<FieldSuggestion[]>(
        `/api/field-suggestions/${encodeURIComponent(fieldKey)}`,
      ),
    // Suggestions evolve slowly; cache for 5 min
    staleTime: 5 * 60_000,
  });

// ─────────────────────────────────────────────────────────────
// SaaS — plans, subscriptions, metrics
// ─────────────────────────────────────────────────────────────
export const saasKeys = {
  all: ["saas"] as const,
  plans: () => [...saasKeys.all, "plans"] as const,
  subscriptions: (filters: { customer_id?: string }) =>
    [...saasKeys.all, "subscriptions", filters] as const,
  mrr: () => [...saasKeys.all, "mrr"] as const,
  arr: () => [...saasKeys.all, "arr"] as const,
  snapshots: () => [...saasKeys.all, "snapshots"] as const,
  burn: () => [...saasKeys.all, "burn"] as const,
  ltvCac: () => [...saasKeys.all, "ltv-cac"] as const,
  // CRM-001a
  components: (planId: string) => [...saasKeys.all, "components", planId] as const,
  overrides: (subId: string) => [...saasKeys.all, "overrides", subId] as const,
};

export const saasPlansQueryOptions = () =>
  queryOptions({
    queryKey: saasKeys.plans(),
    queryFn: () => api<Plan[]>("/api/saas/plans"),
    staleTime: 30_000,
  });

// CRM-001a
export const saasComponentsQueryOptions = (planId: string) =>
  queryOptions({
    queryKey: saasKeys.components(planId),
    queryFn: () =>
      api<PriceComponentOut[]>(`/api/saas/plans/${planId}/components`),
    staleTime: 30_000,
  });

export const saasOverridesQueryOptions = (subId: string) =>
  queryOptions({
    queryKey: saasKeys.overrides(subId),
    queryFn: () =>
      api<SubscriptionPriceOverrideOut[]>(
        `/api/saas/subscriptions/${subId}/price-overrides`,
      ),
    staleTime: 30_000,
  });

export const saasSubscriptionsQueryOptions = (
  filters: { customer_id?: string } = {},
) =>
  queryOptions({
    queryKey: saasKeys.subscriptions(filters),
    queryFn: () => {
      const sp = new URLSearchParams();
      if (filters.customer_id) sp.set("customer_id", filters.customer_id);
      return api<Subscription[]>(`/api/saas/subscriptions?${sp.toString()}`);
    },
    staleTime: 15_000,
  });

export const saasMrrQueryOptions = () =>
  queryOptions({
    queryKey: saasKeys.mrr(),
    queryFn: () => api<{ mrr: string }>("/api/saas/metrics/mrr"),
    staleTime: 30_000,
  });

export const saasArrQueryOptions = () =>
  queryOptions({
    queryKey: saasKeys.arr(),
    queryFn: () => api<{ arr: string }>("/api/saas/metrics/arr"),
    staleTime: 30_000,
  });

export const saasSnapshotsQueryOptions = (limit = 90) =>
  queryOptions({
    queryKey: [...saasKeys.snapshots(), { limit }] as const,
    queryFn: () =>
      api<MetricsSnapshot[]>(`/api/saas/metrics/snapshots?limit=${limit}`),
  });

export interface BurnHeadline {
  snapshot_month: string | null;
  cash_balance_cents: number | null;
  runway_months: number | null;
  net_burn_cents: number | null;
}

export interface BurnResponse {
  months: Array<{
    snapshot_month: string;
    gross_burn_cents: number;
    net_burn_cents: number;
    cash_balance_cents: number;
    runway_months: number | null;
  }>;
  headline: BurnHeadline | null;
}

export const saasBurnQueryOptions = (months = 12) =>
  queryOptions({
    queryKey: [...saasKeys.burn(), { months }] as const,
    queryFn: () => api<BurnResponse>(`/api/saas/metrics/burn?months=${months}`),
    staleTime: 60_000,
  });

export interface LtvCacResponse {
  period: { start: string; end: string };
  ltv: {
    value: number;
    avg_mrr_per_customer: number;
    gross_margin_pct: number;
    avg_lifetime_months: number;
    monthly_churn_rate_pct: number;
  };
  cac: { value: number; sm_expense: number; new_customers: number };
  cac_payback_months: number;
  ltv_cac_ratio: number;
  inputs: {
    subscription_revenue: number;
    cost_of_revenue: number;
    avg_mrr: number;
    avg_active_customers: number;
  };
  warnings: string[];
}

export const saasLtvCacQueryOptions = (startDate: string, endDate: string) =>
  queryOptions({
    queryKey: [...saasKeys.ltvCac(), { startDate, endDate }] as const,
    queryFn: () =>
      api<LtvCacResponse>(
        `/api/saas/metrics/ltv-cac?start_date=${startDate}&end_date=${endDate}`,
      ),
    staleTime: 120_000,
  });

export const saasRecognitionSchedulesQueryOptions = () =>
  queryOptions({
    queryKey: [...saasKeys.all, "recognition", "schedules"] as const,
    queryFn: () =>
      api<RecognitionSchedule[]>("/api/saas/recognition/schedules"),
  });

// ─────────────────────────────────────────────────────────────
// Projects + Phases (lightweight; for autocomplete pickers)
// ─────────────────────────────────────────────────────────────
import type {
  Employee,
  EmploymentStatus,
  PhaseSummary,
  ProgressBillingPage,
  ProjectFull,
  ProjectSummary,
  TimeEntry,
  TimeEntryStatus,
} from "./types";

export interface ProjectListParams {
  status?: string[];
  search?: string;
  davis_bacon_enabled?: boolean;
  limit?: number;
  offset?: number;
}

export interface ProjectListPage<T> {
  items: T[];
  total: number;
  limit: number;
  offset: number;
}

export const projectKeys = {
  all: ["projects"] as const,
  list: (params?: ProjectListParams) =>
    [...projectKeys.all, "list", params ?? {}] as const,
  phases: (projectId: string) =>
    [...projectKeys.all, "phases", projectId] as const,
  progressBillings: (projectId: string) =>
    [...projectKeys.all, "progress-billings", projectId] as const,
};

function buildProjectsQS(params?: ProjectListParams): string {
  const usp = new URLSearchParams();
  if (params?.status?.length) {
    for (const s of params.status) usp.append("status", s);
  }
  if (params?.search) usp.set("search", params.search);
  if (params?.davis_bacon_enabled != null) {
    usp.set("davis_bacon_enabled", String(params.davis_bacon_enabled));
  }
  if (params?.limit != null) usp.set("limit", String(params.limit));
  if (params?.offset != null) usp.set("offset", String(params.offset));
  const qs = usp.toString();
  return qs ? `?${qs}` : "";
}

export const projectsListQueryOptions = (params?: ProjectListParams) =>
  queryOptions({
    queryKey: projectKeys.list(params),
    queryFn: () =>
      api<ProjectListPage<ProjectSummary>>(
        `/api/projects${buildProjectsQS(params)}`,
      ),
    staleTime: 60_000,
  });

/** Full project rows for the /projects admin grid. Uses the same
 * endpoint but typed as ProjectFull so all the new owner_party_id /
 * bill_to_party_id columns are exposed. The picker in time entry,
 * AP invoice, etc. continues to use the lighter ProjectSummary shape. */
export const projectsFullListQueryOptions = (params?: ProjectListParams) =>
  queryOptions({
    queryKey: [...projectKeys.list(params), "full"] as const,
    queryFn: () =>
      api<ProjectListPage<ProjectFull>>(
        `/api/projects${buildProjectsQS(params)}`,
      ),
    staleTime: 30_000,
  });

export const projectPhasesQueryOptions = (projectId: string | null) =>
  queryOptions({
    queryKey: projectKeys.phases(projectId ?? ""),
    queryFn: () => api<PhaseSummary[]>(`/api/projects/${projectId}/phases`),
    enabled: Boolean(projectId),
    staleTime: 60_000,
  });

export const progressBillingsQueryOptions = (
  projectId: string | null,
  params: { limit?: number; offset?: number } = {},
) =>
  queryOptions({
    queryKey: [...projectKeys.progressBillings(projectId ?? ""), params] as const,
    queryFn: () => {
      const search = new URLSearchParams({
        limit: String(params.limit ?? 100),
        offset: String(params.offset ?? 0),
      });
      return api<ProgressBillingPage>(
        `/api/projects/${projectId}/progress-billings?${search.toString()}`,
      );
    },
    enabled: Boolean(projectId),
    staleTime: 30_000,
  });

// ─────────────────────────────────────────────────────────────
// Users — minimal list, used as the approver-picker source.
// ─────────────────────────────────────────────────────────────
export const userKeys = {
  all: ["users"] as const,
  list: () => [...userKeys.all, "list"] as const,
};

export const usersListQueryOptions = () =>
  queryOptions({
    queryKey: userKeys.list(),
    queryFn: () => api<User[]>("/api/users"),
    staleTime: 60_000,
  });

// ─────────────────────────────────────────────────────────────
// Personnel module (Layer 1)
// ─────────────────────────────────────────────────────────────

export const personnelKeys = {
  all: ["personnel"] as const,
  employees: (filters: { employment_status?: EmploymentStatus; include_terminated?: boolean }) =>
    [...personnelKeys.all, "employees", filters] as const,
  employee: (id: string) => [...personnelKeys.all, "employee", id] as const,
  timeEntries: (filters: Record<string, unknown>) =>
    [...personnelKeys.all, "time-entries", filters] as const,
};

export const employeesListQueryOptions = (
  filters: { employment_status?: EmploymentStatus; include_terminated?: boolean } = {},
) =>
  queryOptions({
    queryKey: personnelKeys.employees(filters),
    queryFn: () => {
      const sp = new URLSearchParams();
      if (filters.employment_status) sp.set("employment_status", filters.employment_status);
      if (filters.include_terminated) sp.set("include_terminated", "true");
      return api<Employee[]>(`/api/personnel/employees?${sp.toString()}`);
    },
  });

export const timeEntriesListQueryOptions = (
  filters: {
    employee_id?: string;
    project_id?: string;
    status?: TimeEntryStatus;
    from_date?: string;
    to_date?: string;
    limit?: number;
  } = {},
) =>
  queryOptions({
    queryKey: personnelKeys.timeEntries(filters),
    queryFn: () => {
      const sp = new URLSearchParams();
      for (const [k, v] of Object.entries(filters)) {
        if (v !== undefined && v !== null && v !== "") {
          sp.set(k, String(v));
        }
      }
      return api<TimeEntry[]>(`/api/personnel/time-entries?${sp.toString()}`);
    },
  });

// ─────────────────────────────────────────────────────────────
// Chat surface — list available tools (so the UI can show a hint of
// what to ask). Messages POST is one-shot, not query-cached.
// ─────────────────────────────────────────────────────────────
import type { ChatConversation, ChatConversationMessage, ChatMemory, ChatToolInfo } from "./types";

export const chatKeys = {
  all: ["chat"] as const,
  tools: () => [...chatKeys.all, "tools"] as const,
  conversations: () => [...chatKeys.all, "conversations"] as const,
  messages: (id: string) => [...chatKeys.all, "messages", id] as const,
  memories: () => [...chatKeys.all, "memories"] as const,
};

export const chatToolsQueryOptions = () =>
  queryOptions({
    queryKey: chatKeys.tools(),
    queryFn: () => api<ChatToolInfo[]>("/api/chat/tools"),
    staleTime: 60_000,
  });

export const chatConversationsQueryOptions = () =>
  queryOptions({
    queryKey: chatKeys.conversations(),
    queryFn: () => api<ChatConversation[]>("/api/chat/conversations"),
    staleTime: 0,
  });

export const chatMessagesQueryOptions = (conversationId: string) =>
  queryOptions({
    queryKey: chatKeys.messages(conversationId),
    queryFn: () => api<ChatConversationMessage[]>(`/api/chat/conversations/${conversationId}/messages`),
    staleTime: 0,
  });

export const chatMemoriesQueryOptions = () =>
  queryOptions({
    queryKey: chatKeys.memories(),
    queryFn: () => api<ChatMemory[]>("/api/chat/memories"),
    staleTime: 0,
  });


// ─────────────────────────────────────────────────────────────
// Job cost types (per-entity, customer-defined)
// ─────────────────────────────────────────────────────────────
export interface JobCostType {
  id: string;
  code: string;
  name: string;
  /** The canonical cost type this rolls up to — what actually posts. */
  base_cost_type: string;
  is_active: boolean;
  sort_order: number;
}

export const jobCostTypeKeys = {
  all: ["job-cost-types"] as const,
  list: (includeInactive: boolean) =>
    [...jobCostTypeKeys.all, "list", { includeInactive }] as const,
};

export const jobCostTypesQueryOptions = (includeInactive = false) =>
  queryOptions({
    queryKey: jobCostTypeKeys.list(includeInactive),
    queryFn: () =>
      api<JobCostType[]>(
        includeInactive
          ? "/api/job-cost-types?include_inactive=true"
          : "/api/job-cost-types",
      ),
    staleTime: 30_000,
  });

// ─────────────────────────────────────────────────────────────
// Cost Codes (per-entity taxonomy)
// ─────────────────────────────────────────────────────────────
export interface CostCodeListParams {
  includeInactive?: boolean;
  search?: string;
  postableOnly?: boolean;
  limit?: number;
  offset?: number;
}

export const costCodeKeys = {
  all: ["cost-codes"] as const,
  list: (params?: CostCodeListParams | boolean) =>
    [...costCodeKeys.all, "list", params ?? {}] as const,
  report: (projectId?: string) =>
    projectId
      ? ([...costCodeKeys.all, "report", projectId] as const)
      : ([...costCodeKeys.all, "report"] as const),
};

function buildCostCodesQS(params?: CostCodeListParams): string {
  const qs = new URLSearchParams();
  if (params?.includeInactive) qs.set("include_inactive", "true");
  if (params?.search) qs.set("search", params.search);
  if (params?.postableOnly) qs.set("postable_only", "true");
  if (params?.limit != null) qs.set("limit", String(params.limit));
  if (params?.offset != null) qs.set("offset", String(params.offset));
  const s = qs.toString();
  return s ? `?${s}` : "";
}

/** Paginated / searchable cost-code list (typeahead + admin). */
export const costCodesListQueryOptions = (
  params: CostCodeListParams | boolean = false,
) => {
  // Legacy call sites passed a boolean includeInactive flag.
  const p: CostCodeListParams =
    typeof params === "boolean" ? { includeInactive: params } : (params ?? {});
  return queryOptions({
    queryKey: costCodeKeys.list(p),
    queryFn: () =>
      api<CostCodeListPage>(`/api/cost-codes${buildCostCodesQS(p)}`),
    staleTime: 30_000,
  });
};

/** Cost codes budgeted on a project (job_budgets) — the "On this job"
 *  group in the coding typeahead. Every expense line must carry one of
 *  these (or another entity code, e.g. when budgets lag the field). */
export const projectCostCodesQueryOptions = (
  projectId: string | null | undefined,
  search?: string,
) =>
  queryOptions({
    queryKey: [
      ...costCodeKeys.all, "project", projectId, { search: search ?? "" },
    ] as const,
    queryFn: () => {
      const sp = new URLSearchParams();
      if (search) sp.set("search", search);
      const qs = sp.toString();
      return api<CostCode[]>(
        `/api/projects/${projectId}/cost-codes${qs ? `?${qs}` : ""}`,
      );
    },
    enabled: Boolean(projectId),
    staleTime: 30_000,
  });

export const costCodeMappingsQueryOptions = (
  costCodeId: string | null | undefined,
  includeInactive = false,
) =>
  queryOptions({
    queryKey: [
      ...costCodeKeys.all, "mappings", costCodeId, { includeInactive },
    ] as const,
    queryFn: () =>
      api<CostCodeMapping[]>(
        includeInactive
          ? `/api/cost-codes/${costCodeId}/mappings?include_inactive=true`
          : `/api/cost-codes/${costCodeId}/mappings`,
      ),
    enabled: Boolean(costCodeId),
    staleTime: 30_000,
  });


// ─────────────────────────────────────────────────────────────
// Cost Code Templates (global)
// ─────────────────────────────────────────────────────────────
export const costCodeTemplateKeys = {
  all: ["cost-code-templates"] as const,
  list: () => [...costCodeTemplateKeys.all, "list"] as const,
  detail: (id: string) => [...costCodeTemplateKeys.all, "detail", id] as const,
  nodes: (id: string) => [...costCodeTemplateKeys.all, "nodes", id] as const,
};

export const costCodeTemplatesQueryOptions = () =>
  queryOptions({
    queryKey: costCodeTemplateKeys.list(),
    queryFn: () => api<CostCodeTemplate[]>("/api/cost-code-templates"),
    staleTime: 60_000,
  });

export const costCodeTemplateNodesQueryOptions = (templateId: string | null) =>
  queryOptions({
    queryKey: costCodeTemplateKeys.nodes(templateId ?? ""),
    queryFn: () =>
      api<CostCodeTemplateNode[]>(
        `/api/cost-code-templates/${templateId}/nodes`,
      ),
    enabled: Boolean(templateId),
    staleTime: 60_000,
  });


export const costCodeReportQueryOptions = (projectId?: string) =>
  queryOptions({
    queryKey: costCodeKeys.report(projectId),
    queryFn: () =>
      api<CostCodeReportRow[]>(
        projectId
          ? `/api/reports/cost-code-rollup?project_id=${projectId}`
          : "/api/reports/cost-code-rollup",
      ),
  });


// ─────────────────────────────────────────────────────────────
// Project numbering (per-entity templated codes)
// ─────────────────────────────────────────────────────────────
export const numberingKeys = {
  all: ["numbering"] as const,
  config: () => [...numberingKeys.all, "config"] as const,
  dimensions: () => [...numberingKeys.all, "dimensions"] as const,
};

export const numberingConfigQueryOptions = () =>
  queryOptions({
    queryKey: numberingKeys.config(),
    queryFn: () => api<NumberingConfig | null>("/api/numbering/config"),
    staleTime: 60_000,
  });

export const numberingDimensionsQueryOptions = (includeInactive = false) =>
  queryOptions({
    queryKey: [...numberingKeys.dimensions(), { includeInactive }] as const,
    queryFn: () =>
      api<NumberingDimension[]>(
        includeInactive
          ? "/api/numbering/dimensions?include_inactive=true"
          : "/api/numbering/dimensions",
      ),
    staleTime: 60_000,
  });

// ─────────────────────────────────────────────────────────────
// Purchase Orders
// ─────────────────────────────────────────────────────────────
export const poKeys = {
  all: ["purchase-orders"] as const,
  list: (filters: { vendor_id?: string; status?: string; po_type?: string }) =>
    [...poKeys.all, "list", filters] as const,
  detail: (id: string) => [...poKeys.all, "detail", id] as const,
  receipts: (poId: string) => [...poKeys.all, "receipts", poId] as const,
  approvals: (poId: string) => [...poKeys.all, "approvals", poId] as const,
  releases: (poId: string) => [...poKeys.all, "releases", poId] as const,
  blanketSummary: (poId: string) => [...poKeys.all, "blanket-summary", poId] as const,
};

export const purchaseOrdersListQueryOptions = (
  filters: { vendor_id?: string; status?: string; po_type?: string } = {},
) =>
  queryOptions({
    queryKey: poKeys.list(filters),
    queryFn: () => {
      const sp = new URLSearchParams();
      if (filters.vendor_id) sp.set("vendor_id", filters.vendor_id);
      if (filters.status) sp.set("status", filters.status);
      if (filters.po_type) sp.set("po_type", filters.po_type);
      return api<PurchaseOrder[]>(`/api/purchase-orders?${sp.toString()}`);
    },
  });

export const purchaseOrderDetailQueryOptions = (id: string) =>
  queryOptions({
    queryKey: poKeys.detail(id),
    queryFn: () => api<PurchaseOrder>(`/api/purchase-orders/${id}`),
    enabled: Boolean(id),
  });

export const poReceiptsQueryOptions = (poId: string) =>
  queryOptions({
    queryKey: poKeys.receipts(poId),
    queryFn: () => api<POReceipt[]>(`/api/purchase-orders/${poId}/receipts`),
    enabled: Boolean(poId),
  });

export const poApprovalsQueryOptions = (poId: string) =>
  queryOptions({
    queryKey: poKeys.approvals(poId),
    queryFn: () => api<POApproval[]>(`/api/purchase-orders/${poId}/approvals`),
    enabled: Boolean(poId),
  });

export const poReleasesQueryOptions = (poId: string) =>
  queryOptions({
    queryKey: poKeys.releases(poId),
    queryFn: () => api<PurchaseOrder[]>(`/api/purchase-orders/${poId}/releases`),
    enabled: Boolean(poId),
  });

export const blanketSummaryQueryOptions = (poId: string) =>
  queryOptions({
    queryKey: poKeys.blanketSummary(poId),
    queryFn: () => api<BlanketSummary>(`/api/purchase-orders/${poId}/blanket-summary`),
    enabled: Boolean(poId),
  });

// ─────────────────────────────────────────────────────────────
// Money-core approvals
// ─────────────────────────────────────────────────────────────

export const approvalKeys = {
  all: ["approvals"] as const,
  list: (filters: { state?: string; source_type?: string; mine_only?: boolean; requested_by_me?: boolean }) =>
    [...approvalKeys.all, "list", filters] as const,
  detail: (id: string) => [...approvalKeys.all, "detail", id] as const,
  policies: () => [...approvalKeys.all, "policies"] as const,
  controls: () => [...approvalKeys.all, "controls"] as const,
};

export const approvalRequestsListQueryOptions = (
  filters: { state?: string; source_type?: string; mine_only?: boolean; requested_by_me?: boolean } = {},
) =>
  queryOptions({
    queryKey: approvalKeys.list(filters),
    queryFn: () => {
      const sp = new URLSearchParams();
      if (filters.state) sp.set("state", filters.state);
      if (filters.source_type) sp.set("source_type", filters.source_type);
      if (filters.mine_only !== undefined) {
        sp.set("mine_only", String(filters.mine_only));
      }
      if (filters.requested_by_me !== undefined) {
        sp.set("requested_by_me", String(filters.requested_by_me));
      }
      return api<ApprovalRequest[]>(`/api/money/approvals/requests?${sp.toString()}`);
    },
    staleTime: 15_000,
  });

export const approvalRequestDetailQueryOptions = (id: string) =>
  queryOptions({
    queryKey: approvalKeys.detail(id),
    queryFn: () => api<ApprovalRequestDetail>(`/api/money/approvals/requests/${id}/detail`),
    enabled: Boolean(id),
    staleTime: 60_000, // the first AI artifact is frozen and reused
  });

export const approvalControlsSummaryQueryOptions = () =>
  queryOptions({
    queryKey: approvalKeys.controls(),
    queryFn: () =>
      api<ApprovalControlsSummary>(`/api/money/approvals/controls/summary`),
    staleTime: 30_000,
  });

export const approvalPoliciesQueryOptions = () =>
  queryOptions({
    queryKey: approvalKeys.policies(),
    queryFn: () => api<ApprovalPolicy[]>(`/api/money/approvals/policies`),
    staleTime: 60_000,
  });

export const analyticsQueryOptions = (days = 90) =>
  queryOptions({
    queryKey: [...approvalKeys.all, "analytics", { days }] as const,
    queryFn: () =>
      api<ApprovalAnalytics>(`/api/money/approvals/analytics?days=${days}`),
    staleTime: 60_000,
  });

export const approvalRecommendationsQueryOptions = () =>
  queryOptions({
    queryKey: [...approvalKeys.all, "recommendations"] as const,
    queryFn: () =>
      api<ApprovalRecommendation[]>(
        `/api/money/approvals/policy/recommendations?status=pending`,
      ),
    staleTime: 30_000,
  });

// ─────────────────────────────────────────────────────────────
// Sub Pay Applications (construction pack)
// ─────────────────────────────────────────────────────────────

export const subPayAppKeys = {
  all: ["sub-pay-apps"] as const,
  list: (filters?: Record<string, string>) =>
    [...subPayAppKeys.all, "list", filters] as const,
  detail: (id: string) => [...subPayAppKeys.all, "detail", id] as const,
  byContract: (subcontractId: string) =>
    [...subPayAppKeys.all, "byContract", subcontractId] as const,
};

export const subPayAppsQueryOptions = (filters?: Record<string, string>) =>
  queryOptions({
    queryKey: subPayAppKeys.list(filters),
    queryFn: () => {
      const sp = new URLSearchParams(filters ?? {});
      return api<SubPayAppListItem[]>(`/api/sub-pay-apps?${sp.toString()}`);
    },
    staleTime: 30_000,
  });

export const subPayAppDetailQueryOptions = (id: string) =>
  queryOptions({
    queryKey: subPayAppKeys.detail(id),
    queryFn: () => api<SubPayApplication>(`/api/sub-pay-apps/${id}`),
    staleTime: 15_000,
    enabled: Boolean(id),
  });

export const subPayAppsByContractQueryOptions = (subcontractId: string) =>
  queryOptions({
    queryKey: subPayAppKeys.byContract(subcontractId),
    queryFn: () =>
      api<SubPayAppListItem[]>(
        `/api/sub-pay-apps?subcontract_id=${subcontractId}`,
      ),
    staleTime: 30_000,
    enabled: Boolean(subcontractId),
  });

// ─── Vendor Qualification ────────────────────────────────────────────────────

import type {
  VendorQualification,
  ComplianceAlertQueueItem,
} from "./types";

const qualKeys = {
  detail: (partyId: string) => ["vendor-qual", partyId] as const,
};

export const vendorQualQueryOptions = (partyId: string) =>
  queryOptions({
    queryKey: qualKeys.detail(partyId),
    queryFn: () =>
      api<VendorQualification>(`/api/vendors/${partyId}/qualification`),
    staleTime: 30_000,
    enabled: Boolean(partyId),
  });

// ─── Compliance alert queue ──────────────────────────────────────────────────

const complianceKeys = {
  alertQueue: (status?: string) => ["compliance", "alert-queue", status] as const,
};

export const complianceAlertQueueQueryOptions = (status = "pending") =>
  queryOptions({
    queryKey: complianceKeys.alertQueue(status),
    queryFn: () =>
      api<ComplianceAlertQueueItem[]>(
        `/api/compliance/alert-queue?status=${status}`,
      ),
    staleTime: 20_000,
  });

// ─── Payroll ─────────────────────────────────────────────────────────────────

export const payrollKeys = {
  employees: () => ["payroll", "employees"] as const,
  employee: (id: string) => ["payroll", "employees", id] as const,
  contractors: () => ["payroll", "contractors"] as const,
  payRuns: (status?: string) => ["payroll", "pay-runs", { status }] as const,
  payRun: (id: string) => ["payroll", "pay-runs", id] as const,
  earningCodes: () => ["payroll", "earning-codes"] as const,
};

export const payrollEmployeesQueryOptions = (activeOnly = true) =>
  queryOptions({
    queryKey: [...payrollKeys.employees(), { activeOnly }],
    queryFn: () =>
      api<PayrollEmployee[]>(`/api/payroll/employees?active_only=${activeOnly}`),
  });

export const payrollEmployeeQueryOptions = (id: string) =>
  queryOptions({
    queryKey: payrollKeys.employee(id),
    queryFn: () => api<PayrollEmployee>(`/api/payroll/employees/${id}`),
    enabled: !!id,
  });

export const payRunsQueryOptions = (statusFilter?: string) =>
  queryOptions({
    queryKey: payrollKeys.payRuns(statusFilter),
    queryFn: () => {
      const url = statusFilter
        ? `/api/payroll/pay-runs?status_filter=${statusFilter}`
        : `/api/payroll/pay-runs`;
      return api<PayRun[]>(url);
    },
  });

export const payRunQueryOptions = (id: string) =>
  queryOptions({
    queryKey: payrollKeys.payRun(id),
    queryFn: () => api<PayRun>(`/api/payroll/pay-runs/${id}`),
    enabled: !!id,
  });

export const paychecksQueryOptions = (payRunId: string) =>
  queryOptions({
    queryKey: [...payrollKeys.payRun(payRunId), "paychecks"] as const,
    queryFn: () => api<Paycheck[]>(`/api/payroll/pay-runs/${payRunId}/paychecks`),
    enabled: !!payRunId,
  });

export const earningCodesQueryOptions = () =>
  queryOptions({
    queryKey: payrollKeys.earningCodes(),
    queryFn: () => api<EarningCode[]>(`/api/payroll/earning-codes`),
  });

// ─────────────────────────────────────────────────────────────
// Payroll L4 — Certified Payroll alerts
// ─────────────────────────────────────────────────────────────

export interface CertifiedPayrollAlert {
  type: "wh347_due" | "pw_violations";
  severity: "warning" | "urgent";
  pay_run_id: string;
  pay_run_number: string;
  project_code: string | null;
  message: string;
  action_url: string;
}

export const certifiedPayrollAlertsQueryOptions = () =>
  queryOptions({
    queryKey: ["payroll-l4", "alerts"] as const,
    queryFn: () => api<CertifiedPayrollAlert[]>(`/api/payroll-l4/alerts`),
  });

// ─────────────────────────────────────────────────────────────
// Overdraft / cash-flow alerts
// ─────────────────────────────────────────────────────────────
export interface OverdraftAlert {
  type: "overdraft_fees_charged" | "overdraft_risk" | "cash_shortfall";
  severity: "critical" | "warning";
  title: string;
  message: string;
  advice: string[];
  detail: unknown;
}
export interface OverdraftAlertsResponse {
  as_of: string;
  horizon_days: number;
  total_cash: number;
  projected_low: number;
  balance_basis: string;
  fees_total: number;
  fees_count: number;
  alert_count: number;
  alerts: OverdraftAlert[];
}

export const overdraftAlertsQueryOptions = (buffer = 0, horizonDays = 14) =>
  queryOptions({
    queryKey: ["cash-flow", "alerts", { buffer, horizonDays }] as const,
    queryFn: () =>
      api<OverdraftAlertsResponse>(
        `/api/cash-flow/alerts?buffer=${buffer}&horizon_days=${horizonDays}`,
      ),
    // Each call may hit Plaid for a live balance when a bank is linked — keep it
    // fresh-ish without hammering the bank feed on every dashboard render.
    staleTime: 5 * 60 * 1000,
  });

export interface RecurringCharge {
  merchant: string;
  cadence: string;
  typical_amount: number;
  last_amount: number;
  occurrences: number;
  first_date: string;
  last_date: string;
  predicted_next: string | null;
  total_spent: number;
  annualized: number;
  active: boolean;
  account: string;
  source: string;
}
export interface RecurringChargesResponse {
  source: string;
  plaid_status: string;
  count: number;
  active_count: number;
  monthly_recurring_total: number;
  annualized_total: number;
  charges: RecurringCharge[];
}

export const recurringChargesQueryOptions = () =>
  queryOptions({
    queryKey: ["cash-flow", "recurring"] as const,
    queryFn: () => api<RecurringChargesResponse>(`/api/cash-flow/recurring`),
  });

// ─────────────────────────────────────────────────────────────
// Lien Waivers
// ─────────────────────────────────────────────────────────────
export const lienWaiverKeys = {
  all: ["lien-waivers"] as const,
  list: (filters: { vendor_id?: string; project_id?: string }) =>
    [...lienWaiverKeys.all, "list", filters] as const,
  detail: (id: string) => [...lienWaiverKeys.all, "detail", id] as const,
};

export const lienWaiversListQueryOptions = (
  filters: { vendor_id?: string; project_id?: string } = {},
) =>
  queryOptions({
    queryKey: lienWaiverKeys.list(filters),
    queryFn: () => {
      const sp = new URLSearchParams();
      if (filters.vendor_id) sp.set("vendor_id", filters.vendor_id);
      if (filters.project_id) sp.set("project_id", filters.project_id);
      return api<LienWaiver[]>(`/api/lien-waivers?${sp.toString()}`);
    },
  });

export const lienWaiverTemplateStatesQueryOptions = () =>
  queryOptions({
    queryKey: ["lien-waivers", "templates"],
    queryFn: () =>
      api<LienWaiverStateSummary[]>("/api/lien-waivers/templates"),
    staleTime: Infinity,
  });

export const lienWaiverTemplateQueryOptions = (
  state: string,
  formType: string,
) =>
  queryOptions({
    queryKey: ["lien-waivers", "templates", state, formType],
    queryFn: () =>
      api<LienWaiverTemplateOut>(
        `/api/lien-waivers/templates/${state}/${formType}`,
      ),
    staleTime: Infinity,
    enabled: !!state && !!formType,
  });

// ─────────────────────────────────────────────────────────────
// Subcontracts
// ─────────────────────────────────────────────────────────────
export const subcontractKeys = {
  all: ["subcontracts"] as const,
  list: (filters?: { vendor_id?: string }) =>
    [...subcontractKeys.all, "list", filters] as const,
  detail: (id: string) => [...subcontractKeys.all, "detail", id] as const,
};

export const subcontractsListQueryOptions = (
  filters: { vendor_id?: string } = {},
) =>
  queryOptions({
    queryKey: subcontractKeys.list(filters),
    queryFn: () => {
      const sp = new URLSearchParams();
      if (filters.vendor_id) sp.set("vendor_id", filters.vendor_id);
      return api<Subcontract[]>(`/api/projects/subcontracts/all?${sp.toString()}`);
    },
  });

// ─────────────────────────────────────────────────────────────
// AI Report Builder
// ─────────────────────────────────────────────────────────────
export const reportKeys = {
  all: ["reports"] as const,
  catalog: () => [...reportKeys.all, "catalog"] as const,
  detail: (id: string) => [...reportKeys.all, "detail", id] as const,
  run: (id: string, params: Record<string, unknown>) =>
    [...reportKeys.all, "run", id, params] as const,
};

export interface ReportDefinition {
  id: string;
  entity_id: string | null;
  owner_id: string | null;
  name: string;
  description: string | null;
  category: string;
  visibility: string;
  is_pinned: boolean;
  icon: string | null;
  ai_built: boolean;
  ai_prompt: string | null;
  dsl: Record<string, unknown>;
  created_at: string;
  updated_at: string;
}

export interface CatalogSection {
  category: string;
  label: string;
  reports: ReportDefinition[];
}

export interface CatalogResponse {
  pinned: ReportDefinition[];
  sections: CatalogSection[];
  total: number;
}

export interface ColumnMeta {
  name: string;
  type: string;
  format: string | null;
}

export interface KpiResult {
  label: string;
  value: unknown;
  format: string;
}

export interface ReportRunResult {
  run_id: string;
  report_id: string;
  columns: ColumnMeta[];
  rows: Record<string, unknown>[];
  row_count: number;
  kpis: KpiResult[];
  duration_ms: number;
  cached: boolean;
  params_used: Record<string, unknown>;
}

export interface ClarifyingQuestion {
  id: string;
  question: string;
}

export interface BuildResponse {
  dsl: Record<string, unknown>;
  preview_rows: Record<string, unknown>[];
  columns: ColumnMeta[];
  schema_warnings: string[];
  model: string;
  ai_explanation: string;
  clarifying_questions: ClarifyingQuestion[];
}

export const reportCatalogQueryOptions = () =>
  queryOptions({
    queryKey: reportKeys.catalog(),
    queryFn: () => api<CatalogResponse>("/api/reports/catalog"),
    staleTime: 60_000,
  });

export const reportDetailQueryOptions = (id: string) =>
  queryOptions({
    queryKey: reportKeys.detail(id),
    queryFn: () => api<ReportDefinition>(`/api/reports/${id}`),
    enabled: !!id,
  });

// ─────────────────────────────────────────────────────────────
// Party Addresses
// ─────────────────────────────────────────────────────────────
export const partyAddressKeys = {
  all: ["party-addresses"] as const,
  list: (partyId: string) => [...partyAddressKeys.all, "list", partyId] as const,
};

export const partyAddressesQueryOptions = (partyId: string) =>
  queryOptions({
    queryKey: partyAddressKeys.list(partyId),
    queryFn: () => api<PartyAddress[]>(`/api/parties/${partyId}/addresses`),
    enabled: !!partyId,
  });

// ─────────────────────────────────────────────────────────────
// T&M Tickets
// ─────────────────────────────────────────────────────────────
import type {
  TMTicket, TMTicketListOut,
  RateSheet, RateSheetListOut,
  BillablePreview,
} from "@/lib/types";

export const tmTicketKeys = {
  all: ["tm-tickets"] as const,
  list: (f: Record<string, unknown>) => [...tmTicketKeys.all, "list", f] as const,
  detail: (id: string) => [...tmTicketKeys.all, "detail", id] as const,
  preview: (ticketId: string, rateSheetId: string) =>
    [...tmTicketKeys.all, "preview", ticketId, rateSheetId] as const,
};

export const tmTicketsListQueryOptions = (filters: {
  project_id?: string;
  pco_number?: string;
  status?: string;
  date_from?: string;
  date_to?: string;
} = {}) =>
  queryOptions({
    queryKey: tmTicketKeys.list(filters as Record<string, unknown>),
    queryFn: () => {
      const sp = new URLSearchParams();
      if (filters.project_id) sp.set("project_id", filters.project_id);
      if (filters.pco_number) sp.set("pco_number", filters.pco_number);
      if (filters.status) sp.set("status", filters.status);
      if (filters.date_from) sp.set("date_from", filters.date_from);
      if (filters.date_to) sp.set("date_to", filters.date_to);
      return api<TMTicketListOut>(`/api/money/tm-tickets?${sp.toString()}`);
    },
  });

export const tmTicketDetailQueryOptions = (id: string) =>
  queryOptions({
    queryKey: tmTicketKeys.detail(id),
    queryFn: () => api<TMTicket>(`/api/money/tm-tickets/${id}`),
    enabled: !!id,
  });

export const tmTicketBillablePreviewQueryOptions = (
  ticketId: string,
  rateSheetId: string,
) =>
  queryOptions({
    queryKey: tmTicketKeys.preview(ticketId, rateSheetId),
    queryFn: () =>
      api<BillablePreview>(
        `/api/money/tm-tickets/${ticketId}/billable-preview?rate_sheet_id=${rateSheetId}`,
      ),
    enabled: !!ticketId && !!rateSheetId,
  });

// ─────────────────────────────────────────────────────────────
// Rate Sheets
// ─────────────────────────────────────────────────────────────

export const rateSheetKeys = {
  all: ["rate-sheets"] as const,
  list: (f: Record<string, unknown>) => [...rateSheetKeys.all, "list", f] as const,
  detail: (id: string) => [...rateSheetKeys.all, "detail", id] as const,
};

export const rateSheetsListQueryOptions = (filters: {
  project_id?: string;
  effective_on?: string;
} = {}) =>
  queryOptions({
    queryKey: rateSheetKeys.list(filters as Record<string, unknown>),
    queryFn: () => {
      const sp = new URLSearchParams();
      if (filters.project_id) sp.set("project_id", filters.project_id);
      if (filters.effective_on) sp.set("effective_on", filters.effective_on);
      return api<RateSheetListOut>(`/api/money/rate-sheets?${sp.toString()}`);
    },
  });

export const rateSheetDetailQueryOptions = (id: string) =>
  queryOptions({
    queryKey: rateSheetKeys.detail(id),
    queryFn: () => api<RateSheet>(`/api/money/rate-sheets/${id}`),
    enabled: !!id,
  });

// ─────────────────────────────────────────────────────────────
// T&M Invoices
// ─────────────────────────────────────────────────────────────

import type { TMInvoice, TMInvoiceListOut } from "./types";

export const tmInvoiceKeys = {
  all: ["tm-invoices"] as const,
  list: (f: Record<string, unknown>) => [...tmInvoiceKeys.all, "list", f] as const,
  detail: (id: string) => [...tmInvoiceKeys.all, "detail", id] as const,
};

export const tmInvoicesListQueryOptions = (filters: {
  project_id?: string;
  status?: string;
} = {}) =>
  queryOptions({
    queryKey: tmInvoiceKeys.list(filters as Record<string, unknown>),
    queryFn: () => {
      const sp = new URLSearchParams();
      if (filters.project_id) sp.set("project_id", filters.project_id);
      if (filters.status) sp.set("status", filters.status);
      return api<TMInvoiceListOut>(`/api/money/tm-invoices?${sp.toString()}`);
    },
  });

export const tmInvoiceDetailQueryOptions = (id: string) =>
  queryOptions({
    queryKey: tmInvoiceKeys.detail(id),
    queryFn: () => api<TMInvoice>(`/api/money/tm-invoices/${id}`),
    enabled: !!id,
  });

// ─────────────────────────────────────────────────────────────
// Equipment Costing — Phase 1 (CONSTRUCTION-EQUIPMENT-COSTING-001)
// All endpoints under /api/construction/equipment.
// ─────────────────────────────────────────────────────────────

import type {
  EquipmentItem as EquipmentItemT,
  EquipmentRateChange as EquipmentRateChangeT,
  EquipmentTimeEntry as EquipmentTimeEntryT,
  UnpostedTimeEntriesPage as UnpostedTimeEntriesPageT,
  EquipmentCostPosting as EquipmentCostPostingT,
  EquipmentCostPostingDetail as EquipmentCostPostingDetailT,
  CostByJobReport as CostByJobReportT,
  EquipmentRentalLinksPage as EquipmentRentalLinksPageT,
} from "./types";

export const equipmentItemKeys = {
  all: ["equipment-items"] as const,
  list: (filters?: Record<string, unknown>) =>
    [...equipmentItemKeys.all, "list", filters ?? {}] as const,
  detail: (id: string) => [...equipmentItemKeys.all, "detail", id] as const,
  rateHistory: (id: string) =>
    [...equipmentItemKeys.all, "rate-history", id] as const,
  timeEntries: (id: string, filters?: Record<string, unknown>) =>
    [...equipmentItemKeys.all, "time-entries", id, filters ?? {}] as const,
};

export const equipmentPostingKeys = {
  all: ["equipment-postings"] as const,
  list: (filters?: Record<string, unknown>) =>
    [...equipmentPostingKeys.all, "list", filters ?? {}] as const,
  detail: (id: string) => [...equipmentPostingKeys.all, "detail", id] as const,
  unposted: (filters?: Record<string, unknown>) =>
    [...equipmentPostingKeys.all, "unposted", filters ?? {}] as const,
  costByJob: (filters?: Record<string, unknown>) =>
    [...equipmentPostingKeys.all, "cost-by-job", filters ?? {}] as const,
};

export interface EquipmentItemListFilters {
  ownership_type?: string;
  category?: string;
  search?: string;
  active_only?: boolean;
}

export const equipmentItemsQueryOptions = (
  filters: EquipmentItemListFilters = {},
) =>
  queryOptions({
    queryKey: equipmentItemKeys.list(filters as Record<string, unknown>),
    queryFn: () => {
      const sp = new URLSearchParams();
      if (filters.ownership_type) sp.set("ownership_type", filters.ownership_type);
      if (filters.category) sp.set("category", filters.category);
      if (filters.search) sp.set("search", filters.search);
      if (filters.active_only !== undefined)
        sp.set("active_only", String(filters.active_only));
      const qs = sp.toString();
      return api<EquipmentItemT[]>(
        `/api/construction/equipment${qs ? `?${qs}` : ""}`,
      );
    },
  });

export const equipmentItemDetailQueryOptions = (id: string) =>
  queryOptions({
    queryKey: equipmentItemKeys.detail(id),
    queryFn: () =>
      api<EquipmentItemT>(`/api/construction/equipment/${id}`),
    enabled: !!id,
  });

export const equipmentRateHistoryQueryOptions = (id: string, limit = 50) =>
  queryOptions({
    queryKey: equipmentItemKeys.rateHistory(id),
    queryFn: () =>
      api<EquipmentRateChangeT[]>(
        `/api/construction/equipment/${id}/rate-history?limit=${limit}`,
      ),
    enabled: !!id,
  });

export interface EquipmentTimeEntriesFilters {
  posting_id?: string | "null";
  limit?: number;
}

export const equipmentTimeEntriesQueryOptions = (
  equipmentId: string,
  filters: EquipmentTimeEntriesFilters = {},
) =>
  queryOptions({
    queryKey: equipmentItemKeys.timeEntries(
      equipmentId,
      filters as Record<string, unknown>,
    ),
    queryFn: () => {
      const sp = new URLSearchParams();
      if (filters.posting_id !== undefined)
        sp.set("posting_id", filters.posting_id);
      if (filters.limit !== undefined) sp.set("limit", String(filters.limit));
      const qs = sp.toString();
      return api<EquipmentTimeEntryT[]>(
        `/api/construction/equipment/${equipmentId}/time-entries${qs ? `?${qs}` : ""}`,
      );
    },
    enabled: !!equipmentId,
  });

export interface UnpostedFilters {
  period_id?: string;
  project_id?: string;
  equipment_id?: string;
  cursor?: string;
  limit?: number;
}

export const equipmentUnpostedTimeEntriesQueryOptions = (
  filters: UnpostedFilters = {},
) =>
  queryOptions({
    queryKey: equipmentPostingKeys.unposted(
      filters as Record<string, unknown>,
    ),
    queryFn: () => {
      const sp = new URLSearchParams();
      sp.set("posting_id", "null");
      if (filters.period_id) sp.set("period_id", filters.period_id);
      if (filters.project_id) sp.set("project_id", filters.project_id);
      if (filters.equipment_id) sp.set("equipment_id", filters.equipment_id);
      if (filters.cursor) sp.set("cursor", filters.cursor);
      if (filters.limit !== undefined) sp.set("limit", String(filters.limit));
      return api<UnpostedTimeEntriesPageT>(
        `/api/construction/equipment/time-entries?${sp.toString()}`,
      );
    },
  });

export interface EquipmentCostPostingsFilters {
  period_id?: string;
  status?: string;
  limit?: number;
}

export const equipmentCostPostingsQueryOptions = (
  filters: EquipmentCostPostingsFilters = {},
) =>
  queryOptions({
    queryKey: equipmentPostingKeys.list(filters as Record<string, unknown>),
    queryFn: () => {
      const sp = new URLSearchParams();
      if (filters.period_id) sp.set("period_id", filters.period_id);
      if (filters.status) sp.set("status", filters.status);
      if (filters.limit !== undefined) sp.set("limit", String(filters.limit));
      const qs = sp.toString();
      return api<EquipmentCostPostingT[]>(
        `/api/construction/equipment/cost-postings${qs ? `?${qs}` : ""}`,
      );
    },
  });

export const equipmentCostPostingDetailQueryOptions = (id: string) =>
  queryOptions({
    queryKey: equipmentPostingKeys.detail(id),
    queryFn: () =>
      api<EquipmentCostPostingDetailT>(
        `/api/construction/equipment/cost-postings/${id}`,
      ),
    enabled: !!id,
  });

export interface CostByJobFilters {
  project_id?: string;
  from?: string;
  to?: string;
  include_unposted?: boolean;
}

export const equipmentCostByJobReportQueryOptions = (
  filters: CostByJobFilters = {},
) =>
  queryOptions({
    queryKey: equipmentPostingKeys.costByJob(
      filters as Record<string, unknown>,
    ),
    queryFn: () => {
      const sp = new URLSearchParams();
      if (filters.project_id) sp.set("project_id", filters.project_id);
      if (filters.from) sp.set("from", filters.from);
      if (filters.to) sp.set("to", filters.to);
      if (filters.include_unposted)
        sp.set("include_unposted", String(filters.include_unposted));
      const qs = sp.toString();
      return api<CostByJobReportT>(
        `/api/construction/equipment/reports/cost-by-job${qs ? `?${qs}` : ""}`,
      );
    },
  });

// ─────────────────────────────────────────────────────────────
// Equipment rental links — Phase 2 rental subset
// ─────────────────────────────────────────────────────────────

export const equipmentRentalLinkKeys = {
  all: ["equipment-rental-links"] as const,
  list: (filters?: Record<string, unknown>) =>
    [...equipmentRentalLinkKeys.all, "list", filters ?? {}] as const,
  forItem: (equipmentId: string, filters?: Record<string, unknown>) =>
    [
      ...equipmentRentalLinkKeys.all,
      "for-equipment",
      equipmentId,
      filters ?? {},
    ] as const,
};

export interface EquipmentRentalLinksFilters {
  equipment_id?: string;
  project_id?: string;
  vendor_id?: string;
  from?: string;
  to?: string;
  cursor?: string;
  limit?: number;
}

export const equipmentRentalLinksQueryOptions = (
  filters: EquipmentRentalLinksFilters = {},
) =>
  queryOptions({
    queryKey: equipmentRentalLinkKeys.list(filters as Record<string, unknown>),
    queryFn: () => {
      const sp = new URLSearchParams();
      if (filters.equipment_id) sp.set("equipment_id", filters.equipment_id);
      if (filters.project_id) sp.set("project_id", filters.project_id);
      if (filters.vendor_id) sp.set("vendor_id", filters.vendor_id);
      if (filters.from) sp.set("from", filters.from);
      if (filters.to) sp.set("to", filters.to);
      if (filters.cursor) sp.set("cursor", filters.cursor);
      if (filters.limit !== undefined) sp.set("limit", String(filters.limit));
      const qs = sp.toString();
      return api<EquipmentRentalLinksPageT>(
        `/api/construction/equipment/rental-links${qs ? `?${qs}` : ""}`,
      );
    },
  });

export const equipmentRentalLinksForItemQueryOptions = (
  equipmentId: string,
  filters: Omit<EquipmentRentalLinksFilters, "equipment_id"> = {},
) =>
  queryOptions({
    queryKey: equipmentRentalLinkKeys.forItem(
      equipmentId,
      filters as Record<string, unknown>,
    ),
    queryFn: () => {
      const sp = new URLSearchParams();
      if (filters.project_id) sp.set("project_id", filters.project_id);
      if (filters.from) sp.set("from", filters.from);
      if (filters.to) sp.set("to", filters.to);
      if (filters.cursor) sp.set("cursor", filters.cursor);
      if (filters.limit !== undefined) sp.set("limit", String(filters.limit));
      const qs = sp.toString();
      return api<EquipmentRentalLinksPageT>(
        `/api/construction/equipment/${equipmentId}/rental-links${qs ? `?${qs}` : ""}`,
      );
    },
    enabled: !!equipmentId,
  });

// ── GL Dimensions ─────────────────────────────────────────────────────────────

import type { GlDimension, GlDimensionListItem } from "./types";

export const glDimensionKeys = {
  all:    ["gl-dimensions"] as const,
  list:   () => [...glDimensionKeys.all, "list"] as const,
  detail: (id: string) => [...glDimensionKeys.all, "detail", id] as const,
};

export const glDimensionsQueryOptions = () =>
  queryOptions({
    queryKey: glDimensionKeys.list(),
    queryFn: () => api<GlDimensionListItem[]>("/api/settings/dimensions"),
  });

export const glDimensionDetailQueryOptions = (id: string) =>
  queryOptions({
    queryKey: glDimensionKeys.detail(id),
    queryFn: () => api<GlDimension>(`/api/settings/dimensions/${id}`),
    enabled: !!id,
  });

// ── GMP (Guaranteed Maximum Price) ────────────────────────────────────────────

import type {
  ContingencyStatus,
  GMPAllowance,
  GMPClassification,
  GMPContract,
  OpenBookReport,
  SavingsCalc,
} from "./types";

export const gmpKeys = {
  all:             ["gmp"] as const,
  byProject:       (projectId: string) => [...gmpKeys.all, "project", projectId] as const,
  contract:        (contractId: string) => [...gmpKeys.all, "contract", contractId] as const,
  contingency:     (contractId: string) => [...gmpKeys.all, "contingency", contractId] as const,
  allowances:      (contractId: string) => [...gmpKeys.all, "allowances", contractId] as const,
  classifications: (contractId: string) => [...gmpKeys.all, "classifications", contractId] as const,
  openBook:        (projectId: string, period: string) => [...gmpKeys.all, "open-book", projectId, period] as const,
  savings:         (contractId: string) => [...gmpKeys.all, "savings", contractId] as const,
};

export const gmpContractByProjectQueryOptions = (projectId: string) =>
  queryOptions({
    queryKey: gmpKeys.byProject(projectId),
    queryFn: () => api<GMPContract>(`/api/money/gmp/project/${projectId}`),
    enabled: !!projectId,
    retry: false, // 404 = no contract yet, don't retry
  });

export const gmpContingencyQueryOptions = (contractId: string) =>
  queryOptions({
    queryKey: gmpKeys.contingency(contractId),
    queryFn: () => api<ContingencyStatus>(`/api/money/gmp/${contractId}/contingency`),
    enabled: !!contractId,
  });

export const gmpAllowancesQueryOptions = (contractId: string) =>
  queryOptions({
    queryKey: gmpKeys.allowances(contractId),
    queryFn: () => api<GMPAllowance[]>(`/api/money/gmp/${contractId}/allowances`),
    enabled: !!contractId,
  });

export const gmpClassificationsQueryOptions = (contractId: string) =>
  queryOptions({
    queryKey: gmpKeys.classifications(contractId),
    queryFn: () => api<GMPClassification[]>(`/api/money/gmp/${contractId}/classifications`),
    enabled: !!contractId,
  });

export const gmpOpenBookQueryOptions = (projectId: string, period: string) =>
  queryOptions({
    queryKey: gmpKeys.openBook(projectId, period),
    queryFn: () => api<OpenBookReport>(`/api/money/gmp/project/${projectId}/open-book?period=${period}`),
    enabled: !!projectId && !!period,
  });

export const gmpSavingsQueryOptions = (contractId: string) =>
  queryOptions({
    queryKey: gmpKeys.savings(contractId),
    queryFn: () => api<SavingsCalc>(`/api/money/gmp/${contractId}/savings`),
    enabled: !!contractId,
  });

// ── SaaS — metered usage, dunning, tax rules ─────────────────
export const saasUsageEventsQueryOptions = (subId: string, invoiced?: boolean) =>
  queryOptions({
    queryKey: [...saasKeys.all, "usage", subId, { invoiced }] as const,
    queryFn: () => {
      const sp = new URLSearchParams();
      if (invoiced !== undefined) sp.set("invoiced", String(invoiced));
      const qs = sp.toString();
      return api<import("./types").UsageEvent[]>(
        `/api/saas/subscriptions/${subId}/usage${qs ? "?" + qs : ""}`,
      );
    },
    enabled: !!subId,
  });

export const saasDunningQueryOptions = (subId: string) =>
  queryOptions({
    queryKey: [...saasKeys.all, "dunning", subId] as const,
    queryFn: () =>
      api<import("./types").DunningAttempt[]>(
        `/api/saas/subscriptions/${subId}/dunning`,
      ),
    enabled: !!subId,
  });

export const saasTaxRulesQueryOptions = (jurisdictionCode?: string) =>
  queryOptions({
    queryKey: [...saasKeys.all, "tax-rules", { jurisdictionCode }] as const,
    queryFn: () => {
      const sp = new URLSearchParams();
      if (jurisdictionCode) sp.set("jurisdiction_code", jurisdictionCode);
      const qs = sp.toString();
      return api<import("./types").TaxRule[]>(
        `/api/saas/tax-rules${qs ? "?" + qs : ""}`,
      );
    },
  });

// ─────────────────────────────────────────────────────────────
// AP invoice intake — shared "bills waiting" summary.
// Used by the home dashboard (Needs attention + Bills to review KPI)
// and the AppShell nav badge on Bill intake. Same query key as the
// bills-intake page list so all three share one cache entry.
// ─────────────────────────────────────────────────────────────
export interface ApIntakeSummaryItem {
  id: string;
  status: string;
  created_at: string;
  source_file_original_name?: string | null;
  source_metadata?: { from?: string; subject?: string } | null;
  parsed_payload?: { vendor_name?: string | null } | null;
}

/** Intake statuses that still need a human. Excludes promoted/rejected. */
export const AP_INTAKE_OPEN_STATUSES: readonly string[] = [
  "received",
  "parsing",
  "parsed",
  "awaiting_review",
  "parse_failed",
];

export const apIntakeListQueryOptions = () =>
  queryOptions({
    queryKey: ["ap-intake", "list"] as const,
    queryFn: () =>
      api<{ items: ApIntakeSummaryItem[]; total: number }>(
        "/api/ap/intake?limit=100",
      ),
    // One cache entry for every reader (nav badge, home, workspace). Only the
    // shell polls it (AP_INTAKE_POLL_MS in AppShell): a refetchInterval here
    // would start a timer per mounted reader. A failed read (e.g. no intake
    // permission) is not retried by each reader that mounts.
    staleTime: 15_000,
    retry: false,
    retryOnMount: false,
  });

/** How often the shell refreshes the intake list, so a bill that arrives by
 *  email shows up wherever the user is. */
export const AP_INTAKE_POLL_MS = 30_000;

/** Rows still waiting on a human, newest first. */
export function openIntakeItems(
  items: ApIntakeSummaryItem[] | undefined,
): ApIntakeSummaryItem[] {
  return (items ?? [])
    .filter((r) => AP_INTAKE_OPEN_STATUSES.includes(r.status))
    .sort(
      (a, b) =>
        new Date(b.created_at).getTime() - new Date(a.created_at).getTime(),
    );
}
